import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Database, Brain, Notebook, Lock } from "@phosphor-icons/react";

const CHIPS = [
  { icon: Brain, label: "AI-Assisted Drafting" },
  { icon: Database, label: "Multi-Source Data" },
  { icon: Notebook, label: "Enterprise-Wide Knowledge" },
  { icon: ShieldCheck, label: "Audit & Traceability" },
];

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("cmd@gail.co.in");
  const [password, setPassword] = useState("Sankalak@2026");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try { await login(email, password); navigate("/"); }
    catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen w-full bg-[#0A1128] flex items-center justify-center p-3 sm:p-6">
      <div className="relative w-full max-w-[1400px] rounded-[28px] overflow-hidden shadow-2xl bg-[#0A1128]">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0d1530] via-[#0A1128] to-[#111a38]" />

        {/* GAIL logo top-right */}
        <div className="absolute top-6 right-6 sm:top-8 sm:right-10 z-20">
          <img src="/gail-logo.png" alt="GAIL" className="h-12 sm:h-16 w-auto drop-shadow-lg" />
        </div>

        <div className="relative z-10 grid lg:grid-cols-[1.15fr_0.85fr] gap-8 px-6 sm:px-14 py-12 sm:py-16 min-h-[640px]">
          {/* Left / brand */}
          <div className="fade-up flex flex-col justify-center">
            <h1 className="text-4xl sm:text-6xl font-bold text-[#F59E0B] tracking-tight leading-none">
              <span className="font-serif-report">संकलक</span> <span className="text-white">||</span> Sankalak
            </h1>
            <p className="text-3xl sm:text-5xl text-white font-light mt-3">CMD Dashboard</p>
            <div className="tricolor-bar h-1.5 w-80 max-w-full rounded-full mt-6" />
            <p className="text-slate-300 mt-6 max-w-lg text-base sm:text-lg">
              Secure AI platform for generating &amp; updating CMD management reports from templates, documents and live web sources.
            </p>

            <div className="mt-9 flex items-center gap-5" data-testid="landing-sanchay-block">
              <div className="relative shrink-0">
                <div className="absolute -inset-4 rounded-full bg-[#F59E0B]/20 blur-3xl" />
                <img src="/sanchay-logo.png" alt="Sanchay 2" className="relative h-32 sm:h-40 w-auto drop-shadow-2xl" data-testid="landing-sanchay-logo" />
              </div>
              <div className="border-l border-white/15 pl-5">
                <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400">Powered by</div>
                <div className="text-2xl sm:text-3xl font-bold text-white leading-tight mt-1">Sanchay <span className="text-[#3aa746]">2</span></div>
                <div className="text-slate-400 text-xs sm:text-sm mt-1 max-w-[16rem]">GAIL Knowledge Management &amp; Collaboration Platform</div>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-10 max-w-2xl">
              {CHIPS.map((c) => (
                <div key={c.label} className="rounded-2xl bg-[#0b1230]/75 border border-white/12 px-3 py-5 flex flex-col items-center text-center gap-2.5 backdrop-blur-md shadow-lg">
                  <c.icon size={24} weight="duotone" className="text-[#F59E0B]" />
                  <span className="text-[12px] text-slate-100 leading-tight">{c.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right / sign in */}
          <div className="flex flex-col items-center justify-center fade-up w-full max-w-md mx-auto">
            <button
              onClick={() => {
                const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
                window.location.href = `${backendUrl}/api/auth/azure/login`;
              }}
              className="w-full flex items-center justify-center gap-3 border-2 border-slate-300 bg-white text-slate-700 rounded-xl px-6 py-3 text-lg font-semibold hover:bg-slate-50 hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors shadow-lg"
            >
              <svg width="20" height="20" viewBox="0 0 21 21" aria-hidden="true">
                <rect x="1"  y="1"  width="9" height="9" fill="#F25022" />
                <rect x="11" y="1"  width="9" height="9" fill="#7FBA00" />
                <rect x="1"  y="11" width="9" height="9" fill="#00A4EF" />
                <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
              </svg>
              Sign in with Microsoft
            </button>

            <div className="flex items-center justify-center gap-2 text-slate-400 text-xs mt-6">
              <ShieldCheck size={14} weight="fill" className="text-emerald-500" /> Secure. Compliant. Trusted.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}